import './App.css';
import { useState } from 'react';

function App() {
  const [isDark, setIsDark] = useState(true);

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const SunIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="5"/>
      <path d="m12 1 0 2"/>
      <path d="m12 21 0 2"/>
      <path d="m4.22 4.22 1.42 1.42"/>
      <path d="m18.36 18.36 1.42 1.42"/>
      <path d="m1 12 2 0"/>
      <path d="m21 12 2 0"/>
      <path d="m4.22 19.78 1.42-1.42"/>
      <path d="m18.36 5.64 1.42-1.42"/>
    </svg>
  );

  const MoonIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`}>
      {/* Theme Toggle Button */}
      <div className="fixed top-8 right-8 z-50">
        <button
          onClick={toggleTheme}
          className={`
            p-3 rounded-lg border transition-all duration-300 hover:scale-105 backdrop-blur-md
            ${isDark 
              ? 'border-slate-700/50 bg-black/20 text-white hover:bg-black/30 hover:text-slate-300' 
              : 'border-slate-300/50 bg-white/20 text-black hover:bg-white/30 hover:text-slate-700'
            }
          `}
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-16">
        {/* Header */}
        <header className="flex justify-between items-start mb-20">
          <div>
            <h1 className="text-5xl font-normal mb-3">Guillaume Mourier</h1>
            <p className={`text-2xl font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Head of Product</p>
          </div>
        </header>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20 items-start">
          {/* Left Column - Contact & About */}
          <div className="lg:col-span-1 space-y-12 lg:sticky lg:top-8 lg:self-start">
            <div className={`border-t pt-5 ${isDark ? 'border-slate-700' : 'border-slate-300'}`}>
              <p className={`text-base leading-relaxed mb-12 font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
               With roots in engineering and experience leading cross-functional teams, I work to bridge product, tech, and user needs to build things that work — and last.
              </p>
              
              <div className="space-y-6">
                <div>
                  <a 
                    href="https://linkedin.com/in/gmourier" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`transition-colors text-base ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'}`}
                  >
                    LinkedIn ↗
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Experience */}
          <div className="lg:col-span-2">
            <h2 className={`text-sm font-normal mb-12 tracking-wide border-t border-b py-6 ${isDark ? 'text-white border-slate-700' : 'text-black border-slate-300'}`}>Experience</h2>
            
            <div className="space-y-16">
              {/* Current Role */}
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                  <div className="lg:col-span-2">
                    <h3 className={`text-lg font-normal mb-3 ${isDark ? 'text-white' : 'text-black'}`}>Head of Product</h3>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <a 
                        href="https://www.meilisearch.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`transition-colors ${isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`}
                      >
                        Meilisearch ↗
                      </a>
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      2021 - Present
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Remote & Paris, France
                    </div>
                  </div>
                  
                  <div className="lg:col-span-3">
                    <ul className={`space-y-4 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Build and lead the product function from the ground up as the first PM, overseeing the v1 launch and helping grow the open-source project to 50k+ GitHub stars.</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Define and scale the product strategy across open-source and commercial lines, aligning roadmap, go-to-market, and UX with company goals.</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Drive product-led growth, expanding the customer base and supporting the company’s move toward sustainable revenue.</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Lead a multidisciplinary team (PM, designer, technical writer), and contribute to org-level planning, hiring, budgets, and strategic execution.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Previous Experience */}
              <div className={`border-t pt-16 ${isDark ? 'border-slate-700' : 'border-slate-300'}`}>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                  <div className="lg:col-span-2">
                    <h3 className={`text-lg font-normal mb-3 ${isDark ? 'text-white' : 'text-black'}`}>Senior Web Developer</h3>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Various Companies
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      2015 - 2021
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      France
                    </div>
                  </div>
                  
                  <div className="lg:col-span-3">
                    <ul className={`space-y-4 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Evolved from pure development to acting as a bridge between product, engineering, design, and marketing teams, naturally taking on product ownership responsibilities.</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Gained deep user empathy by directly interfacing with customers and stakeholders, translating their needs into technical requirements and product features.</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Led cross-functional initiatives that required balancing technical constraints with business objectives, developing the strategic thinking that would define my product career.</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>•</span>
                        <span>Mentored junior developers while advocating for user-centered development practices, building the leadership and team management skills essential for product roles.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Education Section */}
              <div className="mt-20">
                <h2 className={`text-sm font-normal mb-12 tracking-wide border-t border-b py-6 ${isDark ? 'text-white border-slate-700' : 'text-black border-slate-300'}`}>Education</h2>
                
                <div>
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-2">
                      <h3 className={`text-lg font-normal mb-3 ${isDark ? 'text-white' : 'text-black'}`}>Lyon, France</h3>
                    </div>
                    
                    <div className={`text-sm lg:col-span-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Master's Degree in Computer Science
                    </div>
                  </div>
                </div>
              </div>

            <div className="lg:col-span-2">
              <h2 className={`text-sm font-normal mb-12 tracking-wide border-t border-b py-6 ${isDark ? 'text-white border-slate-700' : 'text-black border-slate-300'}`}>Music Production</h2>
            
              <div className="space-y-16">
                  <div>
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                      <div className="lg:col-span-2">
                        <h3 className={`text-lg font-normal mb-3 ${isDark ? 'text-white' : 'text-black'}`}>Composer</h3>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          <a 
                            href="https://store.steampowered.com/app/2786760/Another_Door/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`transition-colors ${isDark ? 'hover:text-gray-300' : 'hover:text-gray-800'}`}
                          >
                            Another Door ↗
                          </a>
                        </div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Video Game
                        </div>
                      </div>
                      
                      <div className="lg:col-span-3">
                        <ul className={`space-y-4 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <li className="flex items-start">
                            <span>Another Door is a party game blending gambling mechanics and tough choices. The soundtrack is inspired by 8-bit and retro arcade machines, alternating between ambient tension and fast-paced energy to match the game's shifting dynamics.</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;